<!--
  Doorman v1.2 — payment confirmation panel.

  Renders in the action-pane (above the chat) when
  conversation_state === 'RECEPTION'. v1 routed Reception's
  Confirm / Maybe Later buttons through the chat-window
  quick_actions dropdown — invisible visual hierarchy, no sense
  that this is a commitment moment. v1.2 makes Reception a proper
  payment-confirm surface: emerald icon prominent, price
  prominent, primary/secondary button hierarchy.

  Data sourcing:
    • price prop comes from the active ReaderSkillVariant's
      price_carats (the BE Reception state composes the same
      number into its static intro). Defaults to 25 (legacy minimum).
    • Buttons emit interfaceTrigger with action_types the BE
      Reception state's triggers expect: 'confirm_payment' and
      'maybe_later'.

  Architectural placement matches DoormanMenu.vue: a sibling of
  SelectCards / FourCardReading / OneCardReading, mounted by
  ReaderPage based on conversation_state.
-->
<template>
  <div class="reception-confirm" data-testid="reception-confirm">
    <header class="reception-header">
      <div class="reception-emerald-badge">
        <span class="reception-emerald-icon">💎</span>
        <span class="reception-price">{{ price }}</span>
      </div>
      <h2 class="reception-title">Ready to begin your reading?</h2>
      <p class="reception-subtitle">
        <template v-if="readerName">
          {{ readerName }} is ready to settle in. Confirm to begin.
        </template>
        <template v-else>
          Confirm to begin.
        </template>
      </p>
    </header>

    <div class="reception-actions">
      <button
        type="button"
        class="reception-button reception-confirm-btn"
        data-action="confirm"
        @click="onConfirm"
      >
        Confirm &amp; Pay
      </button>
      <button
        type="button"
        class="reception-button reception-cancel-btn"
        data-action="cancel"
        @click="onMaybeLater"
      >
        Maybe Later
      </button>
    </div>

    <p class="reception-disclaimer">
      Your reading begins immediately after payment.
    </p>
  </div>
</template>

<script setup>
defineProps({
  price: { type: [Number, String], default: 25 },
  readerName: { type: String, default: '' },
})

const emit = defineEmits(['interfaceTrigger'])

function onConfirm() {
  // Backend trigger key matches Reception.define_triggers in
  // reader/service/state_management/state_classes/reception.py.
  emit('interfaceTrigger', 'confirm_payment', {
    button_label: 'Confirm & Pay',
    conversation_type: 'RECEPTION',
    prompt_instructions: 'Confirm & Pay',
  })
}

function onMaybeLater() {
  emit('interfaceTrigger', 'maybe_later', {
    button_label: 'Maybe Later',
    conversation_type: 'RECEPTION',
    prompt_instructions: 'Maybe later',
  })
}
</script>

<style scoped>
.reception-confirm {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px 24px;
  gap: 24px;
  max-width: 420px;
  margin: 0 auto;
}

.reception-header {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.reception-emerald-badge {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 999px;
  background: linear-gradient(
    120deg,
    rgba(0, 255, 127, 0.18) 0%,
    rgba(50, 205, 50, 0.32) 100%
  );
  border: 1px solid rgba(0, 255, 127, 0.45);
  box-shadow: 0 4px 14px rgba(0, 200, 100, 0.25);
}

.reception-emerald-icon {
  font-size: 18px;
}

.reception-price {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
}

.reception-title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #fff;
}

.reception-subtitle {
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  font-style: italic;
  max-width: 320px;
  line-height: 1.5;
}

.reception-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.reception-button {
  width: 100%;
  padding: 14px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s ease, transform 0.1s ease;
}

.reception-button:active {
  transform: translateY(1px);
}

.reception-confirm-btn {
  background: linear-gradient(120deg, #c3a9ff 0%, #663399 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(102, 51, 153, 0.4);
}

.reception-confirm-btn:hover {
  opacity: 0.92;
}

.reception-cancel-btn {
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.4);
  color: rgba(255, 255, 255, 0.85);
}

.reception-cancel-btn:hover {
  background: rgba(195, 169, 255, 0.08);
  border-color: rgba(195, 169, 255, 0.6);
}

.reception-disclaimer {
  margin: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
}
</style>
